/**
 * Rück-Sprung aus Softec — "Angebot aus der Projektmaske wieder öffnen".
 *
 * Der Export merkt sich in Softec je Budget-Struktur die Angebotsnummer
 * (source_offer_id) und — seit dem Parametervertrag 2026-08 — zusätzlich den
 * Slot (sourceQuoteSlot). Der Rückweg kommt über den bestehenden SSO-Weg an:
 *
 *   /?source=softec&openQuoteSlot=<slot>     → Slot ist der EINDEUTIGE Schlüssel,
 *                                              Angebot direkt öffnen.
 *   /?source=softec&openQuoteNumber=<nr>     → Rückfall für den Bestand (dort ist
 *                                              nur die Nummer bekannt). Nummern
 *                                              sind NICHT eindeutig (beim Kunden
 *                                              21 mehrfach vergebene Nummern) —
 *                                              bei mehreren Treffern IMMER die
 *                                              Auswahl zeigen, NIE raten.
 *
 * Bewusst NICHT über buildNachtragBaseEntries (nachtrag-base-selection.ts)
 * aufgelöst: die schließt Nachträge (<Basis>/N<n>) und Entwürfe als Basis aus.
 * Softec kann aber genau die Nummer eines exportierten NACHTRAGS zurückreichen —
 * der muss hier auffindbar sein. Der Kandidaten-Typ und die Summen-Anzeige
 * (formatiereSumme) werden aus dem Modul wiederverwendet, die Treffer-Felder
 * (Nummer, Kunde, Bauvorhaben, Datum, Summe, Slot) sind dieselben wie im
 * Nachtrags-Picker.
 *
 * Reine Ableitungslogik ohne React/Fetch — testbar mit tsx --test
 * (Muster nachtrag-base-selection.ts / dashboard-navigation.ts).
 */

import type { NachtragBaseCandidate } from './nachtrag-base-selection';

/** Obergrenze wie in dashboard-navigation.ts (buildQuoteEditorUrl wirft darüber). */
const MAX_QUOTE_SLOT = 10_000;

export type SoftecRuecksprungRequest =
  | { kind: 'slot'; slot: number }
  | { kind: 'nummer'; nummer: string }
  /** Parameter vorhanden, aber unbrauchbar → verständliche Ansage statt leerem Editor. */
  | { kind: 'ungueltig'; grund: string };

/**
 * Liest die Rück-Sprung-Parameter aus der Such-Zeichenkette der Adresse.
 *
 * - `null` = kein Rück-Sprung (kein source=softec oder keiner der beiden
 *   Parameter vorhanden — z.B. die Nachtrags-Brücke mode=nachtrag bleibt
 *   unberührt).
 * - `openQuoteSlot` hat Vorrang, wenn beide Parameter gesetzt sind (der Slot
 *   ist der eindeutige Schlüssel, die Nummer nur der Bestands-Rückfall).
 * - URL-Kodierung (z.B. A26%2F00025 — Ollis Nummern enthalten "/") löst
 *   URLSearchParams bereits auf.
 */
export function parseSoftecRuecksprung(search: string): SoftecRuecksprungRequest | null {
  const params = new URLSearchParams(search || '');
  if (params.get('source') !== 'softec') return null;

  const rawSlot = params.get('openQuoteSlot');
  const rawNummer = params.get('openQuoteNumber');
  if (rawSlot === null && rawNummer === null) return null;

  if (rawSlot !== null) {
    const trimmed = rawSlot.trim();
    if (!/^[1-9]\d*$/.test(trimmed) || Number(trimmed) > MAX_QUOTE_SLOT) {
      return {
        kind: 'ungueltig',
        grund: `„${rawSlot}" ist keine gültige Ablage-Nummer (erwartet: ganze Zahl 1–${MAX_QUOTE_SLOT}).`,
      };
    }
    return { kind: 'slot', slot: Number(trimmed) };
  }

  const nummer = (rawNummer ?? '').trim();
  if (!nummer) {
    return { kind: 'ungueltig', grund: 'openQuoteNumber ist leer.' };
  }
  return { kind: 'nummer', nummer };
}

/** Ein Treffer der Nummern-Suche — dieselben Felder wie im Nachtrags-Picker. */
export interface RuecksprungTreffer {
  /** Eindeutige Ablage-Nr. — Grundlage des Ladewegs (loadQuoteData). */
  slot: number;
  offerNumber: string;
  customerName: string;
  projectName: string;
  timestamp?: string;
  /** Netto-Angebotssumme; null, wenn nicht ermittelbar. Bei Namensvettern oft das einzige Unterscheidungsmerkmal. */
  totalValue: number | null;
}

export type RuecksprungAufloesung =
  | { status: 'eindeutig'; treffer: RuecksprungTreffer }
  /** Mehrere Angebote tragen diese Nummer → Auswahl zeigen, NIE stillschweigend das erste öffnen. */
  | { status: 'mehrdeutig'; treffer: RuecksprungTreffer[] }
  | { status: 'keiner' };

/**
 * Löst eine Angebotsnummer gegen den Bestand (GET /api/quote-data → files) auf.
 *
 * - Exakter Nummernvergleich nach Trim — KEIN Präfix-/Teilstring-Match: die
 *   Nummer "A26/00025" darf den Nachtrag "A26/00025/N1" nicht mit-treffen
 *   (und umgekehrt findet die Nachtrags-Nummer genau den Nachtrag).
 * - Genau 1 Treffer → direkt öffnen; mehrere → Auswahl; keiner → Ansage.
 * - Sortierung der Auswahl: neuestes zuerst, dann Slot (deterministisch).
 */
export function resolveOpenQuoteNumber(
  files: ReadonlyArray<NachtragBaseCandidate>,
  nummer: string,
): RuecksprungAufloesung {
  const gesucht = (nummer || '').trim();
  if (!gesucht) return { status: 'keiner' };

  const treffer: RuecksprungTreffer[] = (files || [])
    .filter((f): f is NachtragBaseCandidate => !!f && Number.isFinite(f.slot))
    .filter((f) => (f.offerNumber || '').trim() === gesucht)
    .map((f) => ({
      slot: f.slot,
      offerNumber: (f.offerNumber || '').trim(),
      customerName: (f.customerName || '').trim(),
      projectName: (f.projectName || '').trim(),
      timestamp: f.timestamp,
      totalValue:
        typeof f.totalValue === 'number' && Number.isFinite(f.totalValue) ? f.totalValue : null,
    }))
    .sort((a, b) => {
      const byTime = String(b.timestamp || '').localeCompare(String(a.timestamp || ''));
      if (byTime !== 0) return byTime;
      return a.slot - b.slot;
    });

  if (treffer.length === 0) return { status: 'keiner' };
  if (treffer.length === 1) return { status: 'eindeutig', treffer: treffer[0] };
  return { status: 'mehrdeutig', treffer };
}
